import React, { useState, useEffect } from 'react';
import { sliderApi } from '../api/sliderApi';
import { toast } from 'react-toastify';
import {
  Layout, Typography, Button, Card, Row, Col, Input, Select, Space, 
  Tag, Tooltip, Empty, Spin, Modal as AntModal
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined,
  CheckCircleOutlined, ClockCircleOutlined, PictureOutlined, SortAscendingOutlined
} from '@ant-design/icons';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

const SlidersManagement = () => {
  const [sliders, setSliders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSlider, setEditingSlider] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    alignment: 'center',
    image: null,
    priority: 0,
    isActive: true
  });
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    fetchSliders();
  }, []);

  const fetchSliders = async () => {
    try {
      setLoading(true);
      const response = await sliderApi.getAllSliders();
      if (response.success) {
        setSliders(response.data);
      } else {
        toast.error('Failed to load sliders');
      }
    } catch (error) {
      toast.error('Failed to fetch sliders');
      console.error('Error fetching sliders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        image: file
      }));

      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const sliderData = {
        title: formData.title,
        subtitle: formData.subtitle,
        alignment: formData.alignment,
        isActive: formData.isActive,
        priority: formData.priority
      };

      if (editingSlider) {
        const imageFile = formData.image instanceof File ? formData.image : null;
        await sliderApi.updateSlider(editingSlider._id, sliderData, imageFile);
        toast.success('Slider updated successfully');
      } else {
        if (!formData.image) {
          toast.error('Please select an image');
          return;
        }
        await sliderApi.createSlider(sliderData, formData.image);
        toast.success('Slider created successfully');
      }

      fetchSliders();
      resetForm();
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
      toast.error(`Failed: ${errorMessage}`);
      console.error('Error saving slider:', error);
    }
  };

  const handleEdit = (slider) => {
    setEditingSlider(slider);
    setFormData({
      title: slider.title || '',
      subtitle: slider.subtitle || '',
      alignment: slider.alignment || 'center',
      image: null,
      priority: slider.priority || 0,
      isActive: slider.isActive ?? true
    });
    setImagePreview(slider.image);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    AntModal.confirm({
      title: 'Delete Slider',
      content: 'Are you sure you want to delete this slider?',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await sliderApi.deleteSlider(id);
          toast.success('Slider deleted successfully');
          fetchSliders();
        } catch (error) {
          toast.error('Failed to delete slider');
          console.error('Error deleting slider:', error);
        }
      }
    });
  };

  const resetForm = () => {
    setFormData({
      title: '',
      subtitle: '',
      alignment: 'center',
      image: null,
      priority: 0,
      isActive: true
    });
    setImagePreview(null);
    setEditingSlider(null);
    setShowForm(false);
  };

  if (loading) {
    return (
      <Layout style={{ minHeight: '100vh', background: '#ffffff', fontFamily: "'Rubik', sans-serif" }}>
        <Content style={{ padding: '32px 48px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
            <Spin size="large" />
          </div>
        </Content>
      </Layout>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh', background: '#ffffff', fontFamily: "'Rubik', sans-serif" }}>
      <Content style={{ padding: '32px 48px' }}>
        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <div>
            <Title level={2} style={{
              margin: 0,
              color: '#470172',
              fontFamily: "'Rubik', sans-serif",
              fontSize: 32,
              fontWeight: 600
            }}>
              Slider Management
            </Title>
            <Text style={{ fontSize: 16, color: '#595959', fontFamily: "'Rubik', sans-serif" }}>
              Manage homepage slider images and content
            </Text>
          </div>
        </div>

        {/* Stats Cards */}
        <Row gutter={24} style={{ marginBottom: 32 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card bordered={false} style={{ background: '#fff', borderRadius: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{
                  width: 48,
                  height: 48,
                  borderRadius: 8,
                  background: '#f0f0f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <PictureOutlined style={{ fontSize: 24, color: '#470172' }} />
                </div>
                <div>
                  <Text style={{ fontSize: 15, fontFamily: "'Rubik', sans-serif", color: '#595959', display: 'block' }}>
                    Total Sliders
                  </Text>
                  <Text style={{ color: '#470172', fontSize: 32, fontFamily: "'Rubik', sans-serif", fontWeight: 600 }}>
                    {sliders.length}
                  </Text>
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card bordered={false} style={{ background: '#fff', borderRadius: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{
                  width: 48,
                  height: 48,
                  borderRadius: 8,
                  background: '#f6ffed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <CheckCircleOutlined style={{ fontSize: 24, color: '#52c41a' }} />
                </div>
                <div>
                  <Text style={{ fontSize: 15, fontFamily: "'Rubik', sans-serif", color: '#595959', display: 'block' }}>
                    Active Sliders
                  </Text>
                  <Text style={{ color: '#52c41a', fontSize: 32, fontFamily: "'Rubik', sans-serif", fontWeight: 600 }}>
                    {sliders.filter(s => s.isActive).length}
                  </Text>
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card bordered={false} style={{ background: '#fff', borderRadius: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{
                  width: 48,
                  height: 48,
                  borderRadius: 8,
                  background: '#fff1f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <ClockCircleOutlined style={{ fontSize: 24, color: '#f5222d' }} />
                </div>
                <div>
                  <Text style={{ fontSize: 15, fontFamily: "'Rubik', sans-serif", color: '#595959', display: 'block' }}>
                    Inactive Sliders
                  </Text>
                  <Text style={{ color: '#f5222d', fontSize: 32, fontFamily: "'Rubik', sans-serif", fontWeight: 600 }}>
                    {sliders.filter(s => !s.isActive).length}
                  </Text>
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card 
              bordered={false} 
              style={{ 
                background: '#470172', 
                borderRadius: 8,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 100
              }}
              hoverable
              onClick={() => setShowForm(true)}
            >
              <div style={{ textAlign: 'center', padding: '10px 0' }}>
                <PlusOutlined style={{ fontSize: 24, color: '#fff', marginBottom: 8 }} />
                <Text style={{ 
                  fontSize: 16, 
                  fontFamily: "'Rubik', sans-serif", 
                  color: '#fff',
                  fontWeight: 500,
                  display: 'block'
                }}>
                  Add New Slider
                </Text>
              </div>
            </Card>
          </Col>
        </Row>

        {/* Sliders Grid */}
        <Card
          title={
            <div>
              <p style={{ fontFamily: 'Rubik, sans-serif', fontSize: '15px', fontWeight: 400, color: '#470172', margin: '4px 0 0 0', opacity: 0.7 }}>
                List of all sliders
              </p>
            </div>
          }
          extra={
            <Button
              icon={<ReloadOutlined spin={loading} />}
              onClick={fetchSliders}
              disabled={loading}
              style={{
                fontFamily: 'Rubik, sans-serif',
                fontSize: '14px',
                fontWeight: 400,
                color: '#470172',
                borderColor: '#470172'
              }}
            >
              Refresh
            </Button>
          }
          bordered={false}
          style={{ background: '#fff', borderRadius: 8 }}
        >
          {sliders.length === 0 ? (
            <Empty
              description={
                <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>
                  No sliders found. Get started by creating a new slider.
                </span>
              }
            >
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setShowForm(true)}
                size="large"
                style={{
                  fontFamily: 'Rubik, sans-serif',
                  fontSize: '16px',
                  fontWeight: 400,
                  backgroundColor: '#470172',
                  borderColor: '#470172'
                }}
              >
                Add First Slider
              </Button>
            </Empty>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: 24 }}>
              {sliders.map((slider) => (
                <Card
                  key={slider._id}
                  hoverable
                  style={{
                    borderRadius: 8,
                    overflow: 'hidden',
                    border: '1px solid #f0f0f0'
                  }}
                  cover={
                    <div style={{ position: 'relative', height: 200, overflow: 'hidden' }}>
                      <img
                        src={slider.image}
                        alt={slider.title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      <div style={{ position: 'absolute', top: 12, right: 12 }}>
                        <Tag
                          style={{
                            fontSize: 12,
                            fontFamily: "'Rubik', sans-serif",
                            fontWeight: 500,
                            border: 'none',
                            padding: '4px 12px',
                            borderRadius: 6
                          }}
                          color={slider.isActive ? 'success' : 'error'}
                        >
                          {slider.isActive ? 'Active' : 'Inactive'}
                        </Tag>
                      </div>
                      <div style={{ position: 'absolute', top: 12, left: 12 }}>
                        <Tag
                          style={{
                            fontSize: 12,
                            fontFamily: "'Rubik', sans-serif",
                            fontWeight: 500,
                            background: '#470172',
                            color: '#fff',
                            border: 'none',
                            padding: '4px 12px',
                            borderRadius: 6
                          }}
                          icon={<SortAscendingOutlined />}
                        >
                          Priority: {slider.priority}
                        </Tag>
                      </div>
                    </div>
                  }
                >
                  <div style={{ padding: '4px 0' }}>
                    <Title level={5} style={{
                      margin: '0 0 8px 0',
                      fontFamily: "'Rubik', sans-serif",
                      fontSize: 16,
                      fontWeight: 600,
                      color: '#141414'
                    }}>
                      {slider.title}
                    </Title>
                    {slider.subtitle && (
                      <Text style={{
                        display: 'block',
                        marginBottom: 12,
                        fontSize: 13,
                        color: '#595959',
                        fontFamily: "'Rubik', sans-serif"
                      }}>
                        {slider.subtitle}
                      </Text>
                    )}
                    <div style={{ marginBottom: 16 }}>
                      <Tag
                        style={{
                          fontSize: 12,
                          fontFamily: "'Rubik', sans-serif",
                          background: '#fafafa',
                          border: '1px solid #d9d9d9',
                          color: '#595959',
                          borderRadius: 4
                        }}
                      >
                        Alignment: {slider.alignment}
                      </Tag>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                      <Button
                        type="default"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(slider)}
                        style={{
                          color: '#470172',
                          borderColor: '#d9d9d9',
                          fontFamily: "'Rubik', sans-serif",
                          fontSize: 14
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(slider._id)}
                        style={{
                          fontFamily: "'Rubik', sans-serif",
                          fontSize: 14
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </Card>
      </Content>

      {/* Add/Edit Slider Modal */}
      <AntModal
        title={
          <span style={{ fontFamily: "'Rubik', sans-serif", fontSize: 20, fontWeight: 600, color: '#470172' }}>
            {editingSlider ? 'Edit Slider' : 'Add New Slider'}
          </span>
        }
        open={showForm}
        onCancel={resetForm}
        footer={null}
        width={800}
        style={{ fontFamily: "'Rubik', sans-serif" }}
      >
        <form onSubmit={handleSubmit} style={{ marginTop: 24 }}>
          <Row gutter={16}>
            <Col span={12}>
              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ display: 'block', marginBottom: 8, fontFamily: "'Rubik', sans-serif", fontSize: 14 }}>
                  Title <span style={{ color: '#8c8c8c', fontWeight: 400 }}>(Optional)</span>
                </Text>
                <Input
                  size="large"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g., The Hub for Industrial Investment"
                  style={{ fontFamily: "'Rubik', sans-serif" }}
                />
              </div>
            </Col>
            <Col span={12}>
              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ display: 'block', marginBottom: 8, fontFamily: "'Rubik', sans-serif", fontSize: 14 }}>
                  Subtitle
                </Text>
                <Input
                  size="large"
                  name="subtitle"
                  value={formData.subtitle}
                  onChange={handleInputChange}
                  placeholder="Optional subtitle"
                  style={{ fontFamily: "'Rubik', sans-serif" }}
                />
              </div>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ display: 'block', marginBottom: 8, fontFamily: "'Rubik', sans-serif", fontSize: 14 }}>
                  Text Alignment
                </Text>
                <Select
                  size="large"
                  name="alignment"
                  value={formData.alignment}
                  onChange={(value) => setFormData(prev => ({ ...prev, alignment: value }))}
                  style={{ width: '100%', fontFamily: "'Rubik', sans-serif" }}
                >
                  <Option value="left">Left</Option>
                  <Option value="center">Center</Option>
                  <Option value="right">Right</Option>
                </Select>
              </div>
            </Col>
            <Col span={12}>
              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ display: 'block', marginBottom: 8, fontFamily: "'Rubik', sans-serif", fontSize: 14 }}>
                  Priority (Higher = Shows First)
                </Text>
                <Input
                  size="large"
                  type="number"
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  placeholder="0"
                  style={{ fontFamily: "'Rubik', sans-serif" }}
                />
              </div>
            </Col>
          </Row>

          <div style={{ marginBottom: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 8, fontFamily: "'Rubik', sans-serif", fontSize: 14 }}>
              Slider Image <span style={{ color: '#f5222d' }}>*</span>
            </Text>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px dashed #d9d9d9',
                borderRadius: 4,
                cursor: 'pointer',
                fontFamily: "'Rubik', sans-serif",
                fontSize: 14
              }}
            />
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4, fontFamily: "'Rubik', sans-serif" }}>
              Recommended size: 1920x700px for best results
            </Text>
            {imagePreview && (
              <div style={{ marginTop: 12 }}>
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 8, border: '1px solid #f0f0f0' }}
                />
              </div>
            )}
          </div>

          <div style={{ marginBottom: 24, padding: '12px 16px', background: '#ffffff', borderRadius: 6, border: '1px solid #f0f0f0' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontFamily: "'Rubik', sans-serif" }}>
              <input
                type="checkbox"
                id="isActive"
                name="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                style={{ marginRight: 8, width: 16, height: 16, cursor: 'pointer' }}
              />
              <Text strong style={{ fontFamily: "'Rubik', sans-serif" }}>Active Status</Text>
              <Text type="secondary" style={{ marginLeft: 8, fontSize: 12, fontFamily: "'Rubik', sans-serif" }}>
                (Slider will be visible on website)
              </Text>
            </label>
          </div>

          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button
              onClick={resetForm}
              size="large"
              style={{ fontFamily: "'Rubik', sans-serif", fontSize: 14 }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              style={{
                fontFamily: "'Rubik', sans-serif",
                fontSize: 14,
                backgroundColor: '#470172',
                borderColor: '#470172'
              }}
            >
              {editingSlider ? 'Update Slider' : 'Create Slider'}
            </Button>
          </Space>
        </form>
      </AntModal>
    </Layout>
  );
};

export default SlidersManagement;
